/**
 * src/components/teacher-dashboard-components/theme/components/ToggleSwitch.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';

const SwitchContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const SwitchTrack = styled.button`
  position: relative;
  width: ${props => props.$size === 'small' ? '2.25rem' : 
                     props.$size === 'large' ? '3.5rem' : '2.75rem'};
  height: ${props => props.$size === 'small' ? '1.25rem' : 
                      props.$size === 'large' ? '2rem' : '1.5rem'};
  border-radius: ${props => props.theme.borderRadius.full};
  border: none;
  cursor: pointer;
  outline: none;
  background: ${props => props.$checked ? 
    (props.$variant === 'success' ? getColor('semantic.success') :
     props.$variant === 'warning' ? getColor('semantic.warning') :
     props.$variant === 'error' ? getColor('semantic.error') :
     getColor('primary.blue')) : 
    getColor('neutral.medium')};
  ${createTransition('background', 'fast')};
  
  &:focus-visible {
    outline: 2px solid ${getColor('primary.skyBlue')};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => props.theme.isMobile && `
    min-width: 44px;
    min-height: 24px;
  `}
`;

const SwitchThumb = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => props.$checked ? 
    (props.$size === 'small' ? '1.125rem' : 
     props.$size === 'large' ? '1.75rem' : '1.375rem') : '0.125rem'};
  transform: translateY(-50%);
  width: ${props => props.$size === 'small' ? '1rem' : 
                     props.$size === 'large' ? '1.5rem' : '1.25rem'};
  height: ${props => props.$size === 'small' ? '1rem' : 
                      props.$size === 'large' ? '1.5rem' : '1.25rem'};
  background: ${getColor('neutral.white')};
  border-radius: ${props => props.theme.borderRadius.full};
  box-shadow: ${props => props.theme.shadows.sm};
  ${createTransition('left', 'fast')};
`;

const SwitchLabel = styled.span`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.body.fontSize : 
    props.theme.typography.desktop.body.fontSize};
  color: ${getColor('neutral.black')};
  cursor: pointer;
  user-select: none;
`;

/**
 * Toggle Switch.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const ToggleSwitch = ({
  checked = false,
  onChange,
  size = 'medium',
  variant = 'primary',
  disabled = false,
  label,
  labelPosition = 'right',
  className,
  ...props
}) => {
  const theme = useTheme();
  
    /**
   * Handle Toggle.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleToggle = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };
  
  const switchElement = (
    <SwitchTrack
      theme={theme}
      $checked={checked}
      $variant={variant}
      $size={size}
      disabled={disabled}
      onClick={handleToggle}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      {...props}
    >
      <SwitchThumb
        theme={theme}
        $checked={checked}
        $size={size}
      />
    </SwitchTrack>
  );
  
  if (!label) {
    return switchElement;
  }
  
  return (
    <SwitchContainer theme={theme} className={className}>
      {labelPosition === 'left' && (
        <SwitchLabel theme={theme} onClick={handleToggle}>
          {label}
        </SwitchLabel>
      )}
      
      {switchElement}
      
      {labelPosition === 'right' && (
        <SwitchLabel theme={theme} onClick={handleToggle}>
          {label}
        </SwitchLabel>
      )}
    </SwitchContainer>
  );
};

export default ToggleSwitch;
